import React, { useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import {
  formatAppointmentType,
  formatTimeRange,
  getRiskColor,
  getStatusColor,
  getStatusText,
  getRiskTextColor,
} from "../../utils";
import { getHref } from "../../utils/navigation";
import NoRecent from "../../Component/EmptyRecent";
import { Checkbox, Pagination } from "antd";

export default function Index({
  selectedType,
  loading,
  error,
  data,
  setSelectedRows,
  selectedRows,
  sortClicked,
  onVisibleDataChange,
}) {
  const hasData = data && Object.keys(data).length > 0;
  const [appointmentList, setAppointmentList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    if (hasData) {
      const list = Object.entries(data)
        .flatMap(([date, appointments]) => appointments)
        .sort(
          (a, b) =>
            new Date(a.date + "T" + a.startTime) -
            new Date(b.date + "T" + b.startTime)
        );

      // For past appointments, default to most recent first (reverse chronological order)
      // For upcoming appointments, default to earliest first (chronological order)
      let sortedList;
      if (selectedType === "past") {
        // For past appointments: most recent first by default, unless sort is clicked
        sortedList = sortClicked ? list : list.reverse();
      } else {
        // For upcoming appointments: earliest first by default, reverse if sort is clicked
        sortedList = sortClicked ? list.reverse() : list;
      }

      setAppointmentList(sortedList);
    } else {
      setAppointmentList([]);
    }
  }, [data, sortClicked, selectedType, hasData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appointmentList]);

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedList = appointmentList.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    if (onVisibleDataChange) {
      onVisibleDataChange(appointmentList, paginatedList);
    }
  }, [appointmentList, paginatedList, onVisibleDataChange]);

  const isAllPageSelected = paginatedList.length > 0 && paginatedList.every((item) => selectedRows.includes(item.appointmentId));
  const isSomePageSelected = paginatedList.some((item) => selectedRows.includes(item.appointmentId)) && !isAllPageSelected;

  return (
    <div className="overflow-x-auto p-2">
      {loading && (
        <div className="text-center text-gray-500 mt-20">Loading...</div>
      )}
      {appointmentList.length > 0 ? (
        <>
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-gray-100 text-[#171717] font-medium text-sm font-poppins text-left">
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left">
                  <Checkbox
                    checked={isAllPageSelected}
                    indeterminate={isSomePageSelected}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      const pageIds = paginatedList.map((item) => item.appointmentId);
                      setSelectedRows((prev) => {
                        if (isChecked) {
                          const newSelected = [...prev];
                          pageIds.forEach((id) => {
                            if (!newSelected.includes(id)) {
                              newSelected.push(id);
                            }
                          });
                          return newSelected;
                        } else {
                          return prev.filter((id) => !pageIds.includes(id));
                        }
                      });
                    }}
                  />
                </th>
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left">
                  APPT ID
                </th>
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left">
                  MODE
                </th>
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left">
                  STATUS
                </th>
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left">
                  DATE
                </th>
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left">
                  TIME SLOT
                </th>
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left">
                  PATIENT ID
                </th>
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left">
                  PATIENT NAME
                </th>

                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left text-center">
                  RISK
                </th>
                <th className="px-3 py-2 text-[#171717] font-medium text-sm font-poppins text-left"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedList.map((item, idx) => (
                <tr
                  key={idx}
                  className="even:bg-[#F8FAFC] hover:bg-gray-50 transition"
                >
                  <td className="px-3 py-3  text-sm font-poppins underline">
                    <Checkbox
                      checked={selectedRows.includes(item.appointmentId)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setSelectedRows((prev) => {
                          if (isChecked) {
                            return [...prev, item.appointmentId];
                          } else {
                            return prev.filter(
                              (id) => id !== item.appointmentId
                            );
                          }
                        });
                      }}
                    />
                  </td>
                  <td className="px-3 py-3  text-sm font-poppins underline">
                    {item.appointmentId}
                  </td>
                  <td className="px-3 py-3  text-sm font-poppins">
                    {formatAppointmentType(item.mode)}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${getStatusColor(item.status, item.currentStatus)} `}
                      ></span>
                      <span className="text-[#4B61FF] font-semibold text-sm font-poppins">
                        {getStatusText(item.status, item.currentStatus)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3  text-sm font-poppins">{item.date}</td>
                  <td className="px-3 py-3  text-sm font-poppins">
                    {formatTimeRange(item.startTime, item.endTime)}
                  </td>
                  <td className="px-3 py-3  text-sm font-poppins">
                    {item.clientId}
                  </td>
                  <td className="px-3 py-3  text-sm font-poppins">
                    {item.firstName} {item.lastName}
                  </td>

                  <td className="px-3 py-3 text-center">
                    <div
                      className={`inline-block px-2 py-1 text-center ${getRiskTextColor(
                        item?.riskStatus || "N/A"
                      )} rounded-md text-sm font-poppins ${getRiskColor(
                        item.riskStatus
                      )}`}
                    >
                      {getStatusText(item?.riskStatus || "N/A")}
                    </div>
                  </td>
                  <td className="px-1">
                    <a
                      href={getHref(
                        "/appointment-detail/" +
                        item.appointmentId +
                        "/" +
                        item.uid
                      )}
                    >
                      <ChevronRight size={16} className="text-gray-400" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 px-4 py-3 border-t border-[#E2E8F0] gap-4">
            <span className="text-[#8E8E8E] text-sm font-poppins">
              Showing {Math.min(startIndex + 1, appointmentList.length)} to{" "}
              {Math.min(startIndex + pageSize, appointmentList.length)} of{" "}
              {appointmentList.length} entries
            </span>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={appointmentList.length}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
              className="font-poppins text-sm"
            />
          </div>
        </>
      ) : (
        <NoRecent />
      )}
    </div>
  );
}
